import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const timeUntil = (iso) => {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  if (diff <= 0) return null;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (hrs < 24) return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
};

const formatIST = (utcDateString) => {
  const date = new Date(utcDateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// ── Skeleton + Reusable bits ────────────────────────────────────────────────

const SkeletonRow = () => (
  <div className="flex items-center justify-between p-6">
    <div className="space-y-2 flex-1">
      <div className="skeleton h-5 w-48" />
      <div className="skeleton h-4 w-32" />
    </div>
    <div className="skeleton h-9 w-20 rounded-xl" />
  </div>
);

const SkeletonCardGrid = ({ count = 3 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[...Array(count)].map((_, i) => (
      <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="skeleton h-5 w-24 mb-3 rounded-full" />
        <div className="skeleton h-5 w-40 mb-2" />
        <div className="skeleton h-4 w-32 mb-2" />
        <div className="skeleton h-4 w-28 mb-4" />
        <div className="skeleton h-10 w-full rounded-xl" />
      </div>
    ))}
  </div>
);

const TrainerSkeleton = () => (
  <Layout>
    <div className="space-y-8">
      <section>
        <div className="skeleton h-6 w-24 mb-4" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl p-6 bg-gray-200/50">
              <div className="skeleton h-9 w-16 mb-2" />
              <div className="skeleton h-4 w-24" />
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="skeleton h-6 w-44 mb-4" />
        <SkeletonCardGrid count={3} />
      </section>
      <section>
        <div className="skeleton h-6 w-40 mb-4" />
        <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100">
          <SkeletonRow /><SkeletonRow /><SkeletonRow />
        </div>
      </section>
    </div>
  </Layout>
);

const EmptyState = ({ icon, message, hint }) => (
  <div className="flex flex-col items-center justify-center py-14 text-gray-400">
    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">{icon}</div>
    <p className="text-sm font-medium text-gray-500">{message}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const SectionHeader = ({ icon, title, badge, children }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">{icon}</div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {badge !== undefined && badge > 0 && (
        <span className="ml-1 text-xs font-bold text-white bg-red-500 rounded-full w-5 h-5 flex items-center justify-center">{badge}</span>
      )}
    </div>
    {children}
  </div>
);

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  const icons = {
    success: 'M5 13l4 4L19 7',
    error: 'M6 18L18 6M6 6l12 12',
  };

  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Schedule Class Modal ────────────────────────────────────────────────────

const ScheduleClassModal = ({ batches, onSave, onCancel, loading }) => {
  const [form, setForm] = useState({
    batch_id: '',
    title: '',
    description: '',
    scheduled_at: '',
    duration_minutes: 120,
    meeting_link: '',
  });
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.batch_id) return setFormError('Please select a batch.');
    if (!form.title.trim()) return setFormError('Class title is required.');
    if (!form.scheduled_at) return setFormError('Date & time is required.');
    if (!form.duration_minutes || form.duration_minutes < 1) return setFormError('Duration must be at least 1 minute.');

    // Find the selected batch to get its trainer info
    const selectedBatch = batches.find((b) => b.id === form.batch_id);
    const trainerId = selectedBatch?.trainers?.[0]?.id;

    onSave({
      batch_id: form.batch_id,
      trainer_id: trainerId || undefined,
      title: form.title.trim(),
      description: form.description.trim() || null,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
      duration_minutes: Number(form.duration_minutes),
      meeting_link: form.meeting_link.trim() || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Schedule New Class</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Batch</label>
            <select
              name="batch_id"
              value={form.batch_id}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
              required
            >
              <option value="">Select batch</option>
              {batches.map((b) => (
                <option key={b.id} value={b.id}>{b.batch_name} — {b.course_title || 'Course'}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Class Title</label>
            <input
              ref={inputRef}
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. Introduction to React Hooks"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
              placeholder="Brief overview of what will be covered..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Date & Time</label>
              <input
                type="datetime-local"
                name="scheduled_at"
                value={form.scheduled_at}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Duration (minutes)</label>
              <input
                type="number"
                name="duration_minutes"
                value={form.duration_minutes}
                onChange={handleChange}
                min="1"
                max="480"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Meeting Link <span className="text-gray-400 font-normal">(optional)</span></label>
            <input
              type="url"
              name="meeting_link"
              value={form.meeting_link}
              onChange={handleChange}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
              placeholder="e.g. https://meet.google.com/abc-defg-hij"
            />
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Scheduling...' : 'Schedule Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Grade Submission Modal ───────────────────────────────────────────────────

const GradeModal = ({ submission, onSave, onCancel, loading }) => {
  const [grade, setGrade] = useState(submission.grade || '');
  const [feedback, setFeedback] = useState(submission.feedback || '');
  const gradeRef = useRef(null);

  useEffect(() => { gradeRef.current?.focus(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ grade: grade.trim() || null, feedback: feedback.trim() || null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-lg animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h3 className="text-lg font-bold text-gray-900">Grade Submission</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-1.5 text-sm">
            <p className="font-semibold text-gray-900">{submission.student_name || 'Student'}</p>
            <p className="text-gray-500">{submission.assignment_title} &middot; {submission.batch_name || 'Batch'}</p>
            <p className="text-xs text-gray-400">Submitted {formatIST(submission.submitted_at)}</p>
            {submission.submission_text && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-500 mb-1">Submission:</p>
                <p className="text-gray-700 text-sm whitespace-pre-wrap">{submission.submission_text}</p>
              </div>
            )}
            {submission.attachment_url && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <a
                  href={submission.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary text-sm hover:underline flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  View Attachment
                </a>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Grade <span className="text-gray-400 font-normal">(e.g. A, B+, 85, Pass)</span>
              </label>
              <input
                ref={gradeRef}
                type="text"
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                placeholder="Enter grade..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Feedback <span className="text-gray-400 font-normal">(optional)</span></label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
                placeholder="Add feedback for the student..."
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-white bg-[#ffb547] rounded-xl hover:bg-[#ffb547]/90 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Grade'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

const TrainerDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Schedule class modal
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Grade modal
  const [grading, setGrading] = useState(null);
  const [gradeLoading, setGradeLoading] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchAll = useCallback(async () => {
    try {
      const [classesRes, batchesRes, assignmentsRes, attendanceRes, materialsRes] = await Promise.all([
        api.get('/api/classes'),
        api.get('/api/batches'),
        api.get('/api/assignments'),
        api.get('/api/attendance'),
        api.get('/api/materials'),
      ]);

      const allClasses = classesRes.data || [];
      const batches = batchesRes.data || [];
      const assignments = assignmentsRes.data || [];
      const attendanceRecords = attendanceRes.data || [];
      const materials = materialsRes.data || [];

      // Phase 2 — fetch student counts per batch + submissions for grading
      const batchStudentCounts = {};
      const studentCountPromises = batches.map((b) =>
        api.get(`/api/batches/${b.id}/students`).then((res) => {
          batchStudentCounts[b.id] = (res.data || []).length;
        }).catch(() => {
          batchStudentCounts[b.id] = 0;
        })
      );

      const withSubmissions = assignments.filter((a) => a.submission_count > 0);
      let allSubmissions = [];
      const submissionPromises = withSubmissions.slice(0, 10).map((a) =>
        api.get(`/api/assignments/${a.id}/submissions`).then((res) => {
          allSubmissions = allSubmissions.concat(
            (res.data || []).map((s) => ({ ...s, batch_name: a.batch_name, class_title: a.class_title }))
          );
        }).catch(() => {})
      );

      await Promise.all([...studentCountPromises, ...submissionPromises]);

      // Derived data
      const now = new Date();
      const upcoming = allClasses
        .filter((c) => c.status === 'scheduled' && new Date(c.scheduled_at) > new Date(now - 60 * 60 * 1000))
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
        .slice(0, 5);

      const completedClasses = allClasses.filter((c) => c.status === 'completed');
      const totalPresent = attendanceRecords.filter((r) => r.status === 'present' || r.status === 'late').length;
      const avgAttendance = attendanceRecords.length > 0
        ? Math.round((totalPresent / attendanceRecords.length) * 100)
        : 0;

      const pendingSubmissions = allSubmissions.filter((s) => !s.grade && s.grade !== 0);
      const totalStudents = Object.values(batchStudentCounts).reduce((sum, c) => sum + c, 0);

      // Enrich batches with student count + next class date
      const enrichedBatches = batches.map((b) => {
        const nextClass = allClasses
          .filter((c) => c.batch_id === b.id && c.status === 'scheduled' && new Date(c.scheduled_at) > now)
          .sort((a, bb) => new Date(a.scheduled_at) - new Date(bb.scheduled_at))[0];
        return {
          ...b,
          student_count: batchStudentCounts[b.id] || 0,
          next_class: nextClass || null,
        };
      });

      setData({
        upcoming,
        batches: enrichedBatches,
        pendingSubmissions,
        materials,
        stats: {
          classesTaught: completedClasses.length,
          totalStudents,
          pendingCount: pendingSubmissions.length,
          avgAttendance,
        },
      });
    } catch (err) {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleScheduleClass = async (form) => {
    setScheduleLoading(true);
    try {
      await api.post('/api/classes', form);
      setShowSchedule(false);
      showToast('Class scheduled successfully');
      // Refresh data to show new class
      setLoading(true);
      await fetchAll();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to schedule class'), 'error');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleGrade = async ({ grade, feedback }) => {
    if (!grading) return;
    setGradeLoading(true);
    try {
      await api.put(`/api/assignments/submissions/${grading.id}/grade`, { grade, feedback });
      // Remove from pending list
      setData((prev) => ({
        ...prev,
        pendingSubmissions: prev.pendingSubmissions.filter((s) => s.id !== grading.id),
        stats: { ...prev.stats, pendingCount: prev.stats.pendingCount - 1 },
      }));
      setGrading(null);
      showToast('Submission graded successfully');
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to save grade'), 'error');
    } finally {
      setGradeLoading(false);
    }
  };

  if (loading) return <TrainerSkeleton />;
  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const { upcoming, batches, pendingSubmissions, materials, stats } = data;

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-8 animate-fade-in">
        {/* ── Quick Stats ──────────────────────────────────────────────── */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                label: 'Classes Taught', value: stats.classesTaught, gradient: 'from-[#20C997] to-[#1B9AAA]',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
              },
              {
                label: 'Total Students', value: stats.totalStudents, gradient: 'from-[#003479] to-[#005792]',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />,
              },
              {
                label: 'Pending Grades', value: stats.pendingCount, gradient: 'from-[#ffb547] to-[#ffd166]',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />,
              },
              {
                label: 'Avg Attendance', value: `${stats.avgAttendance}%`, gradient: 'from-[#13263c] to-[#003479]',
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
              },
            ].map((card, i) => (
              <div key={card.label} className={`bg-gradient-to-br ${card.gradient} rounded-2xl p-6 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-200`} style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold">{card.value}</p>
                    <p className="text-sm opacity-90 mt-1">{card.label}</p>
                  </div>
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">{card.icon}</svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Upcoming Classes + Schedule ───────────────────────────────── */}
        <section>
          <SectionHeader
            icon={
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            title="My Upcoming Classes"
          >
            <button
              onClick={() => setShowSchedule(true)}
              className="px-4 py-2 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Schedule Class
            </button>
          </SectionHeader>

          {upcoming.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState
                icon={
                  <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
                message="No upcoming classes scheduled"
                hint="Use the Schedule Class button to create a new class"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcoming.map((cls) => {
                const remaining = timeUntil(cls.scheduled_at);
                return (
                  <div key={cls.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 p-6 flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                        {cls.batch_name || 'Batch'}
                      </span>
                      {remaining && (
                        <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {remaining}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">{cls.title}</h3>
                    <p className="text-sm text-gray-500 mb-1">
                      {formatIST(cls.scheduled_at)}
                    </p>
                    <p className="text-sm text-gray-400 mb-4">
                      {cls.course_title || 'Course'} &middot; {cls.duration_minutes || 60} min
                    </p>
                    <div className="mt-auto">
                      {cls.meeting_link ? (
                        <a
                          href={cls.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex w-full items-center justify-center gap-2 py-2.5 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M14.5 8.5v7l4.5 3V5.5l-4.5 3zM2 7.5v9A1.5 1.5 0 003.5 18h9a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0012.5 6h-9A1.5 1.5 0 002 7.5z" />
                          </svg>
                          Join Class
                        </a>
                      ) : (
                        <button disabled className="w-full py-2.5 bg-gray-100 text-gray-400 font-medium rounded-xl text-sm cursor-not-allowed">
                          {remaining ? `Starts in ${remaining}` : 'Scheduled'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Pending Submissions ───────────────────────────────────────── */}
        <section>
          <SectionHeader
            icon={
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            }
            title="Pending Submissions"
            badge={pendingSubmissions.length}
          />

          {pendingSubmissions.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState
                icon={
                  <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                message="All caught up — no pending submissions"
                hint="When students submit assignments, they'll appear here for grading"
              />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
              {pendingSubmissions.slice(0, 10).map((sub) => (
                <div key={sub.id} className="flex items-center justify-between p-6 hover:bg-gray-50/50 transition-colors">
                  <div className="flex-1 min-w-0 mr-4">
                    <h3 className="font-semibold text-gray-900 truncate">{sub.student_name || 'Student'}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {sub.assignment_title} &middot; {sub.batch_name || 'Batch'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Submitted {formatIST(sub.submitted_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => setGrading(sub)}
                    className="px-4 py-2 bg-[#ffb547] text-white text-sm font-medium rounded-xl hover:bg-[#ffb547]/90 transition-colors flex-shrink-0"
                  >
                    Grade
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── My Batches ───────────────────────────────────────────────── */}
        <section>
          <SectionHeader
            icon={
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            }
            title="My Batches"
          />

          {batches.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState
                icon={
                  <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                }
                message="No batches assigned yet"
                hint="Batches will appear here once assigned by admin"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {batches.map((batch) => (
                <div key={batch.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 p-6 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <span className="text-xs font-medium text-secondary bg-secondary/10 px-2.5 py-1 rounded-full">
                      {batch.course_title || 'Course'}
                    </span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {batch.student_count}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{batch.batch_name}</h3>
                  <p className="text-sm text-gray-500 mb-1">
                    {formatIST(batch.start_date)} &ndash; {formatIST(batch.end_date)}
                  </p>
                  {batch.next_class ? (
                    <p className="text-sm text-primary mb-4">
                      Next: {formatIST(batch.next_class.scheduled_at)}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 mb-4">No upcoming classes</p>
                  )}
                  <div className="mt-auto">
                    <button
                      onClick={() => navigate(`/reports?batch_id=${batch.id}`)}
                      className="w-full py-2.5 bg-primary/10 text-primary font-medium rounded-xl hover:bg-primary/20 transition-colors text-sm"
                    >
                      View Students
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        {/* ── My Materials ──────────────────────────────────────────────── */}
        <section>
          <SectionHeader
            icon={
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
            title="My Materials"
          />

          {materials.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
              <EmptyState
                icon={
                  <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                }
                message="No materials added yet"
                hint="Use the Materials page to add folder links for your students"
              />
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(
                materials.reduce((groups, m) => {
                  const key = m.batch_name || 'General';
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(m);
                  return groups;
                }, {})
              ).map(([batchName, items]) => (
                <div key={batchName}>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{batchName}</h3>
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm divide-y divide-gray-100">
                    {items.map((m) => (
                      <div key={m.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50/50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{m.title}</p>
                            {m.class_title && (
                              <p className="text-xs text-gray-400 truncate">{m.class_title}</p>
                            )}
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-4">{formatIST(m.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ── Schedule Class Modal ──────────────────────────────────────── */}
      {showSchedule && (
        <ScheduleClassModal
          batches={batches}
          onSave={handleScheduleClass}
          onCancel={() => setShowSchedule(false)}
          loading={scheduleLoading}
        />
      )}

      {/* ── Grade Submission Modal ─────────────────────────────────────── */}
      {grading && (
        <GradeModal
          submission={grading}
          onSave={handleGrade}
          onCancel={() => setGrading(null)}
          loading={gradeLoading}
        />
      )}
    </Layout>
  );
};

export default TrainerDashboard;
